<template>
  <PublicFormLayout>
    <PublicFormNotice v-if="isLoading" tone="loading" :title="t('common.loading')" />

    <PublicFormNotice v-else-if="loadError" tone="danger" :title="loadError" />

    <template v-else>
      <div class="tw:mb-7">
        <h1 class="tw:mt-0! tw:mb-2! tw:text-20! tw:font-bold! tw:text-gray-900">{{ title }}</h1>
        <p class="tw:m-0! tw:text-14 tw:text-gray-600">{{ introText }}</p>
      </div>

      <div
        ref="messagesElement"
        class="tw:flex tw:max-h-[420px] tw:flex-col tw:gap-4 tw:overflow-y-auto tw:overscroll-contain tw:rounded-md tw:bg-gray-50 tw:p-4 tw:sm:p-5">
        <div
          v-for="message in messages"
          :key="message.id"
          class="tw:flex tw:max-w-[85%] tw:flex-col tw:gap-1"
          :class="message.direction === 'inbound' ? 'tw:items-end tw:self-end' : 'tw:items-start tw:self-start'">
          <!-- body is escaped by linkifyText -->
          <div
            class="chat-message-body tw:rounded-[12px] tw:px-3.5 tw:py-2.5 tw:text-14 tw:wrap-break-word"
            :class="
              message.direction === 'inbound'
                ? 'tw:rounded-br-[4px] tw:bg-primary tw:text-white'
                : 'tw:rounded-bl-[4px] tw:border tw:border-solid tw:border-gray-200 tw:bg-white tw:text-gray-900'
            "
            v-html="linkifyText(message.body)"></div>
          <span class="tw:px-1 tw:text-11 tw:text-gray-500">{{
            formatDateTime(message.created_at, t('date.format_datetime'))
          }}</span>
        </div>

        <p v-if="messages.length === 0" class="tw:m-0! tw:py-6 tw:text-center tw:text-14 tw:text-gray-500">
          {{ t('candidate.public_chat.no_messages') }}
        </p>
      </div>

      <form class="tw:mt-6" @submit.prevent="sendReply">
        <LeadTextarea
          v-model="replyBody"
          :rows="3"
          :label="t('candidate.public_chat.reply_label')"
          :placeholder="t('candidate.public_chat.reply_placeholder')"
          :disabled="isSending"
          :errors="replyError"
          @keydown.enter.ctrl="sendReply"
          @keydown.enter.meta="sendReply" />
        <LeadButton
          type="submit"
          variant="primary"
          block
          class="tw:mt-5!"
          :loading="isSending"
          :disabled="!replyBody.trim()">
          {{ t('candidate.public_chat.send') }}
        </LeadButton>
      </form>
    </template>
  </PublicFormLayout>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, shallowRef } from 'vue';
import { useRoute } from 'vue-router';
import { ClientError } from '@/api/client';
import { fetchChatThread, isOrderFinishedError, postChatReply } from '@/api/publicForm';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import LeadTextarea from '@/components/lead-design/LeadTextarea.vue';
import PublicFormLayout from '@/components/candidate/public/PublicFormLayout.vue';
import PublicFormNotice from '@/components/candidate/public/PublicFormNotice.vue';
import { formatDateTime } from '@/filters/dateFormat';
import { linkifyText } from '@/func/string/linkify';
import { createPostingTranslator } from '@/locale/translator';
import type { PublicChatMessage } from '@/types/candidate';

/* ---------------------------------- STATE --------------------------------- */
const route = useRoute();
const token = route.params.token as string;

// replaced by the posting's language once the thread is loaded
const postingTranslator = shallowRef(createPostingTranslator(null));

const isLoading = ref(true);
const loadError = ref<string | null>(null);
const companyName = ref<string | null>(null);
const messages = ref<PublicChatMessage[]>([]);
const messagesElement = ref<HTMLElement | null>(null);

const replyBody = ref('');
const isSending = ref(false);
const replyError = ref<string | null>(null);

/* -------------------------------- COMPUTED -------------------------------- */
const title = computed(() =>
  companyName.value
    ? t('candidate.public_chat.title_company', { company: companyName.value })
    : t('candidate.public_chat.title')
);

const introText = computed(() =>
  companyName.value
    ? t('candidate.public_chat.intro', { company: companyName.value })
    : t('candidate.public_chat.intro_no_company')
);

/* --------------------------------- METHODS -------------------------------- */
function t(key: string, params?: { [key: string]: string | number }): string {
  return postingTranslator.value.get(key, params);
}

async function scrollToBottom(): Promise<void> {
  await nextTick();
  if (messagesElement.value) {
    messagesElement.value.scrollTop = messagesElement.value.scrollHeight;
  }
}

async function loadThread(): Promise<void> {
  try {
    const thread = await fetchChatThread(token);
    messages.value = thread.messages;
    companyName.value = thread.company_name;
    postingTranslator.value = createPostingTranslator(thread.language);
  } catch (error) {
    if (isOrderFinishedError(error)) {
      loadError.value = t('candidate.public_chat.order_finished');
    } else if (error instanceof ClientError && error.response.status === 404) {
      loadError.value = t('candidate.public_chat.error_invalid');
    } else if (error instanceof ClientError) {
      loadError.value = t('candidate.public.error_generic');
    } else {
      loadError.value = t('common.network_error');
    }
  } finally {
    isLoading.value = false;
  }
  await scrollToBottom();
}

async function sendReply(): Promise<void> {
  const text = replyBody.value.trim();
  if (!text || isSending.value) return;

  isSending.value = true;
  replyError.value = null;

  try {
    messages.value.push(await postChatReply(token, text));
    replyBody.value = '';
    await scrollToBottom();
  } catch (error) {
    if (isOrderFinishedError(error)) {
      loadError.value = t('candidate.public_chat.order_finished');
      return;
    }

    replyError.value =
      error instanceof ClientError ? t('candidate.public_chat.reply_error') : t('common.network_error');
  } finally {
    isSending.value = false;
  }
}

/* ----------------------------- LIFECYCLE HOOKS ---------------------------- */
onMounted(loadThread);
</script>

<style scoped>
.chat-message-body :deep(a) {
  color: inherit;
  text-decoration: underline;
}
</style>
